/**
 * useOfflineQueue - React hook for offline queue management
 * Provides queue state and operations for components
 */
import { useState, useEffect, useCallback } from 'react';
import { offlineQueueManager, OPERATION_TYPES, OPERATION_ACTIONS } from '../utils/offlineQueueManager';

export const useOfflineQueue = () => {
  const [queueState, setQueueState] = useState(offlineQueueManager?.getState());

  useEffect(() => {
    // Subscribe to queue changes
    const unsubscribe = offlineQueueManager?.addListener((state) => {
      setQueueState(state);
    });

    return unsubscribe;
  }, []);

  /**
   * Queue a bookmark operation
   */
  const queueBookmark = useCallback((action, userId, storyId) => {
    return offlineQueueManager?.enqueue(
      OPERATION_TYPES?.BOOKMARK,
      'bookmarks',
      action,
      {
        user_id: userId,
        story_id: storyId,
        created_at: new Date()?.toISOString(),
      }
    );
  }, []);

  /**
   * Queue a clap operation
   */
  const queueClap = useCallback((storyId, clapIncrement = 1) => {
    return offlineQueueManager?.enqueue(
      OPERATION_TYPES?.CLAP,
      'stories',
      OPERATION_ACTIONS?.UPDATE,
      {
        story_id: storyId,
      },
      {
        clapIncrement,
      }
    );
  }, []);

  /**
   * Queue a comment operation
   */
  const queueComment = useCallback((storyId, authorId, content) => {
    return offlineQueueManager?.enqueue(
      OPERATION_TYPES?.COMMENT,
      'comments',
      OPERATION_ACTIONS?.INSERT,
      {
        story_id: storyId,
        author_id: authorId,
        content,
        created_at: new Date()?.toISOString(),
      }
    );
  }, []);

  /**
   * Queue a story update operation
   */
  const queueStoryUpdate = useCallback((storyId, storyData, isNew = false) => {
    return offlineQueueManager?.enqueue(
      OPERATION_TYPES?.STORY_UPDATE,
      'stories',
      isNew ? OPERATION_ACTIONS?.INSERT : OPERATION_ACTIONS?.UPDATE,
      {
        id: storyId,
        ...storyData,
        updated_at: new Date()?.toISOString(),
      }
    );
  }, []);

  /**
   * Manually trigger queue replay
   */
  const syncNow = useCallback(async () => {
    await offlineQueueManager?.replayQueue();
  }, []);

  /**
   * Clear all queued operations
   */
  const clearQueue = useCallback(() => {
    offlineQueueManager?.clearQueue();
  }, []);

  /**
   * Clear failed operations
   */
  const clearFailedOperations = useCallback(() => {
    offlineQueueManager?.clearFailedOperations();
  }, []);

  /**
   * Resolve conflict manually
   */
  const resolveConflict = useCallback(async (operationId, resolution) => {
    offlineQueueManager?.resolveConflict(operationId, resolution);
    
    // If resolution is to use queued data, trigger sync
    if (resolution === 'use_queued') {
      await offlineQueueManager?.replayQueue();
    }
  }, []);

  /**
   * Get conflict operations count
   */
  const conflictCount = queueState?.queue?.filter(op => op?.status === 'conflict')?.length || 0;

  return {
    // State
    isOnline: queueState?.isOnline,
    connectionQualityGood: queueState?.connectionQualityGood,
    pendingCount: queueState?.pendingCount,
    isReplaying: queueState?.isReplaying,
    queue: queueState?.queue,
    conflictCount,
    batchProgress: queueState?.batchProgress,
    
    // Operations
    queueBookmark,
    queueClap,
    queueComment,
    queueStoryUpdate,
    syncNow,
    clearQueue,
    clearFailedOperations,
    resolveConflict,
  };
};
